import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { UserCircle, ClipboardList, MessageCircleHeart, Share2, Lock } from "lucide-react";
import ClientShell from "./ClientShell";
import { JourneyRail } from "./JourneyRail";
import { useMyWork, useShareReflection } from "@/lib/teamWorkQueries";
import { MyAuditResults } from "./shared/MyAuditResults";

/**
 * Master v5 Prompt 22 — Team/Corporate "My Work": ONLY the signed-in
 * member's own private material (practice, attempts, reflections, cues).
 * Every section is sourced server-side by the CALLING actor's own
 * membership_id — never another member's, regardless of role.
 */
export default function MyWorkPage() {
  const { workspaceId } = useParams();
  const { data, isError } = useMyWork(workspaceId);
  const shareReflection = useShareReflection(workspaceId);

  if (isError) return <ClientShell><p className="text-ink-soft" data-testid="my-work-error">This Workspace's My Work isn't available.</p></ClientShell>;
  if (!data) {
    return (
      <ClientShell>
        <p className="text-ink-soft" data-testid="my-work-loading">Loading My Work…</p>
      </ClientShell>
    );
  }

  async function handleToggleShare(rflId, currentlyShared) {
    try {
      await shareReflection.mutateAsync({ rflId, choice: currentlyShared ? "only_me_and_coach" : "my_team_and_coach" });
      toast.success(currentlyShared ? "Reflection is private again" : "Reflection shared with the team");
    } catch (err) {
      toast.error(err.message === "request_failed" ? "Couldn't update sharing" : err.message);
    }
  }

  return (
    <ClientShell>
      <div className="max-w-3xl" data-testid="my-work-page">
        <div className="flex items-center gap-2 text-terracotta mb-1">
          <UserCircle size={18} />
          <span className="text-xs uppercase tracking-wide font-medium">Only visible to you & your coach</span>
        </div>
        <h1 className="font-serif text-3xl text-ink mb-8">My Work</h1>

        <MyAuditResults workspaceId={workspaceId} />

        <div className="border border-ink/15 bg-white/40 rounded-xl p-6 mb-6">
          <p className="text-xs uppercase tracking-wide text-terracotta font-medium mb-4">Your journey</p>
          <JourneyRail
            nodes={data.journey_rail}
            onNodeClick={null}
            emptyLabel="No private journey stages yet."
            testIdPrefix="my-work-journey-rail"
          />
        </div>

        <div className="border border-ink/15 bg-white/50 rounded-lg p-5 mb-6" data-testid="my-work-assignments">
          <div className="flex items-center gap-2 text-ink-soft mb-3"><ClipboardList size={16} /><span className="text-sm uppercase tracking-wide">Your assignments</span></div>
          {data.my_assignments.length === 0 ? (
            <p className="text-sm text-ink-soft">No private assignments yet.</p>
          ) : (
            <ul className="space-y-2">
              {data.my_assignments.map((a) => (
                <li key={a.asg_id} className="text-sm text-ink" data-testid={`my-work-assignment-${a.asg_id}`}>{a.title_snapshot}</li>
              ))}
            </ul>
          )}
        </div>

        <div className="border border-ink/15 bg-white/50 rounded-lg p-5" data-testid="my-work-reflections">
          <div className="flex items-center gap-2 text-ink-soft mb-3"><MessageCircleHeart size={16} /><span className="text-sm uppercase tracking-wide">Your reflections</span></div>
          {data.my_reflections.length === 0 ? (
            <p className="text-sm text-ink-soft">No reflections yet.</p>
          ) : (
            <ul className="space-y-3">
              {data.my_reflections.map((rf) => (
                <li key={rf.rfl_id} className="flex items-start justify-between gap-3 text-sm text-ink border-l-2 border-ink/10 pl-3" data-testid={`my-work-reflection-${rf.rfl_id}`}>
                  <span>{rf.response_text}</span>
                  <button
                    onClick={() => handleToggleShare(rf.rfl_id, rf.is_shared_with_team)}
                    disabled={shareReflection.isPending}
                    className="shrink-0 inline-flex items-center gap-1 text-xs font-medium text-terracotta hover:text-terracotta/80 disabled:opacity-50"
                    data-testid={`my-work-reflection-share-toggle-${rf.rfl_id}`}
                  >
                    {rf.is_shared_with_team ? <><Lock size={12} /> Make private</> : <><Share2 size={12} /> Share with team</>}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </ClientShell>
  );
}
